import { useMemo } from 'react';
import { LOADING_COMPLIANCE_LIMIT_PCT, LOADING_LIMIT_LABEL, LOADING_WARNING_LABEL, LOADING_WARNING_PCT } from './loadingConstants';
import { useThemeStore } from '../store/themeStore';
import { parseUtcTimestampMs } from './timeUtils';

// ── Constants ─────────────────────────────────────────────────────────────

export const ELEMENT_PALETTE = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
];

export interface TimeRangeDef {
  label: string;
  /** Backend `since` shortcut (MonitoringControllerBase.AllowedTimeIntervals). */
  value: string;
  hours: number;
}

/**
 * Time range shortcuts of the monitoring page. ONE picker drives every tab
 * (charts, since-window cards, weekly verdict tables, solver history) — never
 * add a second time control inside a tab. Up to one year, like the archive
 * picker below; the backend buckets long ranges (bucketLabelForRange).
 */
export const TIME_RANGES: TimeRangeDef[] = [
  { label: '2h', value: '2h', hours: 2 },
  { label: '6h', value: '6h', hours: 6 },
  { label: '12h', value: '12h', hours: 12 },
  { label: '24h', value: '24h', hours: 24 },
  { label: '7T', value: '7d', hours: 7 * 24 },
  { label: '30T', value: '30d', hours: 30 * 24 },
  { label: '90T', value: '90d', hours: 90 * 24 },
  { label: '1J', value: '1y', hours: 365 * 24 },
];

/** Hours covered by a live range value (undefined for an unknown value). */
export function timeRangeHours(value: string): number | undefined {
  return TIME_RANGES.find(r => r.value === value)?.hours;
}

/**
 * Server-side bucket of a live range, for chart subtitles — mirrors
 * MonitoringControllerBase.GetBucketInterval. null = raw samples.
 */
export function bucketLabelForRange(value: string): string | null {
  switch (value) {
    case '12h': return '2-Min-Buckets';
    case '24h': return '5-Min-Buckets';
    case '7d': return '15-Min-Buckets';
    case '30d': return '1-h-Buckets';
    case '90d': return '3-h-Buckets';
    case '1y': return '12-h-Buckets';
    default: return null;
  }
}

/**
 * Time windows for ARCHIVE zones: anchored at the END of the snapshot archive
 * (not wall-clock now — the archived period lies in the past). Same labels as
 * the live picker (sub-day windows make no sense on 15-min archive frames);
 * hours=null means the full archive range.
 */
export const ARCHIVE_TIME_RANGES: { label: string; value: string; hours: number | null }[] = [
  ...TIME_RANGES.filter(r => r.hours >= 24),
  { label: 'Alles', value: 'all', hours: null },
];

// ── Helpers ───────────────────────────────────────────────────────────────

export function getElementColor(elementId: string, elementIds: string[]): string {
  const idx = elementIds.indexOf(elementId);
  return ELEMENT_PALETTE[idx % ELEMENT_PALETTE.length];
}

export type NameMap = Record<string, string>;

export function displayName(elementId: string, nameMap: NameMap): string {
  if (nameMap[elementId]) return nameMap[elementId];
  if (elementId.startsWith('_ring_')) return 'Ring: ' + elementId.slice(6);
  return elementId.length > 16 ? '...' + elementId.slice(-12) : elementId;
}

export function cableLabel(name: string): string {
  const m = name.match(/Kabel\s+\d+/i);
  return m ? m[0] : name;
}

/** Parse a backend timestamp string to epoch ms (thin wrapper over timeUtils). */
export function parseTs(ts: string): number {
  return parseUtcTimestampMs(ts);
}

/**
 * Minimum time-scale unit for a series: multi-day ranges must use day ticks.
 * Without this Chart.js keeps the hour unit and autoskip picks strides that
 * are not day-aligned (e.g. every 73rd hourly tick on a 30-day window) — the
 * HH:mm labels then read as a nonsensical hour progression.
 */
export function timeScaleMinUnit(timestamps: string[]): 'minute' | 'day' {
  if (timestamps.length < 2) return 'minute';
  let min = Infinity;
  let max = -Infinity;
  for (const t of timestamps) {
    const ms = parseTs(t);
    if (ms < min) min = ms;
    if (ms > max) max = ms;
  }
  return max - min > 3 * 24 * 3600_000 ? 'day' : 'minute';
}

/**
 * Return a Set of indices where a gap exists *before* that index.
 * A gap is detected when the jump exceeds 1.8× the median interval.
 */
export function detectGapIndices(sortedTimestamps: string[]): Set<number> {
  if (sortedTimestamps.length < 2) return new Set();
  const times = sortedTimestamps.map(parseTs);
  const deltas = times.slice(1).map((t, i) => t - times[i]);
  const sorted = [...deltas].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  // Floor at 20 min so normal 15-min snapshot intervals never trigger a false gap
  const threshold = Math.max(median * 1.8, 20 * 60_000);
  const gaps = new Set<number>();
  for (let i = 1; i < times.length; i++) {
    if (times[i] - times[i - 1] > threshold) gaps.add(i);
  }
  return gaps;
}

export type XY = { x: number; y: number | null };

/**
 * Build a time-series array of {x, y} points from sorted timestamps and a
 * value-getter.  At detected gaps a null-y point is inserted at the midpoint
 * so Chart.js (with spanGaps:false) visually breaks the line.
 */
export function buildTimeSeriesWithGaps(
  sortedTimestamps: string[],
  gapIndices: Set<number>,
  getValue: (ts: string) => number | null,
): XY[] {
  const points: XY[] = [];
  const times = sortedTimestamps.map(parseTs);
  for (let i = 0; i < sortedTimestamps.length; i++) {
    if (gapIndices.has(i)) {
      // Insert a null point in the middle of the gap to break the line
      const mid = (times[i - 1] + times[i]) / 2;
      points.push({ x: mid, y: null });
    }
    points.push({ x: times[i], y: getValue(sortedTimestamps[i]) });
  }
  return points;
}

// ── Time axis: date-aware tick labels ─────────────────────────────────────

/**
 * Chart.js time-scale tick callback that re-introduces the date on time-only
 * axes. Chart.js auto-selects the tick unit; with hour/minute ticks the
 * labels are 'HH:mm' only, so any range crossing midnight (≥14 h in practice)
 * showed times without dates. This keeps the compact time label but appends
 * the date as a second line on the first tick and on every day change —
 * only when the visible ticks actually span more than one calendar day.
 * Day-or-coarser units (labels without ':') already carry the date untouched.
 *
 * IMPORTANT: Chart.js passes the raw tick VALUE (epoch ms) as `label` when using
 * time scales, not the pre-formatted string. We must format it ourselves.
 */
export function dateAwareTickCallback(
  label: string | number,
  index: number,
  ticks: { value: number }[],
): string | string[] {
  const tick = ticks[index];
  if (!tick) return String(label);

  const tickDate = new Date(tick.value);

  // Multi-week ranges: Chart.js may KEEP the hour unit and let autoskip pick
  // strides that are not day-aligned (e.g. every 73rd hourly tick on a 30-day
  // window) — the HH:mm labels then read as a nonsensical hour progression.
  // When the effective tick spacing is day-or-coarser, label with the DATE.
  if (ticks.length > 1) {
    const spanMs = ticks[ticks.length - 1].value - ticks[0].value;
    const spacingMs = spanMs / (ticks.length - 1);
    if (spacingMs >= 20 * 3600_000) {
      const withYear = spanMs > 300 * 24 * 3600_000;
      return tickDate.toLocaleDateString('de-DE', withYear
        ? { day: '2-digit', month: '2-digit', year: '2-digit' }
        : { day: '2-digit', month: '2-digit' });
    }
  }

  // Chart.js time scale passes raw epoch ms as `label` — format it to HH:mm
  // (matches the displayFormats.hour/minute config in all monitoring charts).
  const text = typeof label === 'number'
    ? tickDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : String(label);

  // Day-or-coarser units (labels without ':') already carry the date untouched.
  if (!text.includes(':')) return text;

  if (ticks.length < 2) return text;
  const first = new Date(ticks[0].value);
  const last = new Date(ticks[ticks.length - 1].value);
  if (first.toDateString() === last.toDateString()) return text; // single day — time suffices
  if (index > 0 && new Date(ticks[index - 1].value).toDateString() === tickDate.toDateString()) {
    return text; // same day as previous tick — no date repetition
  }
  return [text, tickDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })];
}

// ── Theme-aware chart colors ────────────────────────────────────────────

export interface ChartColors {
  text: string;
  textSecondary: string;
  gridLine: string;
  zeroLine: string;
  crosshair: string;
  tooltipBg: string;
  tooltipTitle: string;
  tooltipBody: string;
  tooltipBorder: string;
}

export function useChartColors(): ChartColors {
  const theme = useThemeStore(s => s.theme);
  return useMemo(() => {
    if (theme === 'dark') {
      return {
        text: '#f1f5f9',
        textSecondary: '#b8c4d4',
        gridLine: 'rgba(148, 163, 184, 0.15)',
        zeroLine: 'rgba(148, 163, 184, 0.45)',
        crosshair: 'rgba(148, 163, 184, 0.3)',
        tooltipBg: 'rgba(30, 41, 59, 0.96)',
        tooltipTitle: '#f1f5f9',
        tooltipBody: '#e2e8f0',
        tooltipBorder: 'rgba(148, 163, 184, 0.25)',
      };
    }
    return {
      text: '#0f172a',
      textSecondary: '#475569',
      gridLine: 'rgba(0, 0, 0, 0.08)',
      zeroLine: 'rgba(0, 0, 0, 0.25)',
      crosshair: 'rgba(0, 0, 0, 0.15)',
      tooltipBg: 'rgba(255, 255, 255, 0.97)',
      tooltipTitle: '#0f172a',
      tooltipBody: '#334155',
      tooltipBorder: 'rgba(0, 0, 0, 0.1)',
    };
  }, [theme]);
}

export function tooltipStyle(colors: ChartColors) {
  return {
    backgroundColor: colors.tooltipBg,
    titleColor: colors.tooltipTitle,
    bodyColor: colors.tooltipBody,
    borderColor: colors.tooltipBorder,
    borderWidth: 1,
    padding: 10,
    cornerRadius: 8,
    titleFont: { size: 12, weight: 'bold' as const },
    bodyFont: { size: 12 },
  };
}

// ── Reusable Chart.js inline plugins ─────────────────────────────────────

export function makeZeroLinePlugin(zeroLineColor: string) {
  return {
    id: 'zeroLine',
      afterDraw(chart: any) {
      const yScale = chart.scales?.y;
      if (!yScale) return;
      const yPixel = yScale.getPixelForValue(0);
      if (yPixel < yScale.top || yPixel > yScale.bottom) return;
      const ctx = chart.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = zeroLineColor;
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.moveTo(chart.chartArea.left, yPixel);
      ctx.lineTo(chart.chartArea.right, yPixel);
      ctx.stroke();
      ctx.restore();
    },
  };
}

/**
 * Vertical position of a threshold label pill: above its line by default,
 * below when that would overlap a pill already placed (e.g. 80 % and 90 %
 * lines a few pixels apart). Pills are 16 px high; `placed` collects the
 * occupied [top, bottom] ranges.
 */
export function placeThresholdPill(lineY: number, placed: Array<[number, number]>): number {
  const above = lineY - 18;
  const collides = (top: number) => placed.some(([t, b]) => top < b + 2 && top + 16 > t - 2);
  const y = collides(above) ? lineY + 3 : above;
  placed.push([y, y + 16]);
  return y;
}

/**
 * Loading-threshold plugin shared by the realtime and forecast trafo charts:
 * green/orange/red background zones plus dashed lines with label pills at
 * LOADING_WARNING_PCT (§14a EnWG Gefährdungsschwelle) and
 * LOADING_COMPLIANCE_LIMIT_PCT (DIN EN 60076-7 Überlast).
 */
export function makeTrafoLoadingThresholdPlugin() {
  return {
    id: 'trafoThresholds',

    beforeDatasetsDraw(chart: any) {
      const yScale = chart.scales?.y;
      if (!yScale) return;
      const ctx = chart.ctx;
      const { left, right, top, bottom } = chart.chartArea;

      // Colored background zones (drawn behind data lines)
      const yLimit = Math.max(yScale.getPixelForValue(LOADING_COMPLIANCE_LIMIT_PCT), top);
      const yWarn = Math.min(Math.max(yScale.getPixelForValue(LOADING_WARNING_PCT), top), bottom);
      const yBottom = bottom;

      ctx.save();

      // Green zone: 0 – warning
      if (yWarn < yBottom) {
        ctx.fillStyle = 'rgba(34, 197, 94, 0.06)';
        ctx.fillRect(left, yWarn, right - left, yBottom - yWarn);
      }

      // Orange zone: warning – legal limit
      if (yLimit < yWarn) {
        ctx.fillStyle = 'rgba(245, 158, 11, 0.08)';
        ctx.fillRect(left, yLimit, right - left, yWarn - yLimit);
      }

      // Red zone: above the legal limit
      if (yLimit > top) {
        ctx.fillStyle = 'rgba(239, 68, 68, 0.08)';
        ctx.fillRect(left, top, right - left, yLimit - top);
      }

      ctx.restore();
    },

    afterDatasetsDraw(chart: any) {
      const yScale = chart.scales?.y;
      if (!yScale) return;
      const ctx = chart.ctx;
      const { left, right, top } = chart.chartArea;

      // Threshold lines + inline labels (drawn on top of data)
      const thresholds = [
        { value: LOADING_COMPLIANCE_LIMIT_PCT, color: '#EF4444', label: `${LOADING_LIMIT_LABEL} — Überlast` },
        { value: LOADING_WARNING_PCT, color: '#F59E0B', label: `${LOADING_WARNING_LABEL} — Gefährdung` },
      ];
      const placedPills: Array<[number, number]> = [];
      for (const { value, color, label } of thresholds) {
        const y = yScale.getPixelForValue(value);
        if (y < top - 2 || y > chart.chartArea.bottom + 2) continue;
        ctx.save();
        // Dashed line
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.setLineDash([8, 4]);
        ctx.lineWidth = 1.5;
        ctx.moveTo(left, y);
        ctx.lineTo(right, y);
        ctx.stroke();
        // Label inside chart, top-right with background pill (never overlapping)
        ctx.font = '10px Inter, system-ui, sans-serif';
        const textW = ctx.measureText(label).width;
        const pillX = right - textW - 16;
        const pillY = placeThresholdPill(y, placedPills);
        ctx.fillStyle = color + '18';
        ctx.beginPath();
        ctx.roundRect(pillX, pillY, textW + 12, 16, 4);
        ctx.fill();
        ctx.fillStyle = color;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, pillX + 6, pillY + 8);
        ctx.restore();
      }
    },
  };
}

export function makeCrosshairPlugin(crosshairColor: string) {
  return {
    id: 'crosshair',
      afterDraw(chart: any) {
      const tooltip = chart.tooltip;
      if (!tooltip || !tooltip.opacity) return;
      const x = tooltip.caretX;
      const ctx = chart.ctx;
      const { top, bottom } = chart.chartArea;
      ctx.save();
      ctx.beginPath();
      ctx.strokeStyle = crosshairColor;
      ctx.setLineDash([3, 3]);
      ctx.lineWidth = 1;
      ctx.moveTo(x, top);
      ctx.lineTo(x, bottom);
      ctx.stroke();
      ctx.restore();
    },
  };
}
